<template>
  <div id="client-info">
    <div
      class="d-flex justify-content-center align-items-center client-id"
    >
      <strong class="finflux-id">#{{ client.finfluxId }}</strong>
    </div>

    <div class="info">
      <div class="name"><center><strong>{{ client.nameVi }}</strong></center></div>
      <table class="details">
        <tr>
          <td>{{ $t('BRANCH') }}</td>
          <td>{{ getBranchName(client.branchId) }}</td>
        </tr>
        <tr>
          <td>{{ $t('FIRST_NAME') }}</td>
          <td>{{ client.firstName }}</td>
        </tr>
        <tr>
          <td>{{ $t('LAST_NAME') }}</td>
          <td>{{ client.lastName }}</td>
        </tr>
        <tr>
          <td>{{ $t('CITIZEN_ID') }}</td>
          <td>{{ client.citizenId }}</td>
        </tr>
        <tr>
          <td>{{ $t('ZALO_ID') }}</td>
          <td>{{ client.zaloId }}</td>
        </tr>
        <tr>
          <td>
            <span>{{ $t('PRIMARY_PHONE_NUMBER') }}</span>
          </td>
          <td>
            <span>{{ client.primaryPhoneNumber }}</span>
            <font-awesome-icon @click="startCall(client.primaryPhoneNumber, client.id)" class="call" :icon="faPhoneAlt" />
          </td>
        </tr>
        <tr v-if="!!client.secondaryPhoneNumber">
          <td>
            <span>{{ $t('SECONDARY_PHONE_NUMBER') }}</span>
          </td>
          <td>
            <span>{{ client.secondaryPhoneNumber }}</span>
            <font-awesome-icon @click="startCall(client.secondaryPhoneNumber, client.id)" class="call" :icon="faPhoneAlt" />
          </td>
        </tr>
        <tr>
          <td>{{ $t('ADDRESS') }}</td>
          <td>{{ client.fullAddress }}</td>
        </tr>
        <tr>
          <td>{{ $t('DATE_OF_BIRTH') }}</td>
          <td>{{ moment(client.dateOfBirth).format(DATE_FORMAT) }}</td>
        </tr>
        <tr>
          <td>{{ $t('GENDER') }}</td>
          <td>{{ $t(client.gender) }}</td>
        </tr>
        <tr>
          <td>{{ $t('NUMBER_OF_ACTIVE_CONTRACTS') }}</td>
          <td>{{ client.numberOfActiveContracts }}/{{ client.numberOfContracts }}</td>
        </tr>
        <tr>
          <td>{{ $t('CREATED_DATE') }}</td>
          <td>{{ moment(client.createdAt).format('YYYY-MM-DD, HH:mm') }}</td>
        </tr>
        <tr>
          <td>{{ $t('CREATED_BY') }}</td>
          <td>{{ client.createdBy ? `${client.createdBy.firstName} ${client.createdBy.lastName}` : '' }}</td>
        </tr>
        <tr>
          <td>{{ $t('LAST_MODIFIED') }}</td>
          <td>{{ moment(client.modifiedAt).format('YYYY-MM-DD, HH:mm') }}</td>
        </tr>
        <tr>
          <td>{{ $t('MODIFIED_BY') }}</td>
          <td>{{ client.modifiedBy ? `${client.modifiedBy.firstName} ${client.modifiedBy.lastName}` : '' }}</td>
        </tr>
      </table>
    </div>

    <hr />

    <div class="d-flex justify-content-center align-items-center action-button">
      <primary-button class="yellow" @click="showEditClient()" :text="$t('EDIT')" />
    </div>

    <edit-client-info />
  </div>
</template>

<script>
import { onMounted } from '@vue/composition-api';
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPhoneAlt } from '@fortawesome/free-solid-svg-icons';

import PrimaryButton from '@/components/PrimaryButton';

import { DATE_FORMAT, DISPLAY_DATE_FORMAT, CALL_REFS } from '@/consts';

import EditClientInfo from '../EditClientInfo';

const { useState, useActions } = createNamespacedHelpers('clientDetail');
const { useActions: useModalActions } = createNamespacedHelpers('modal');
const { useActions: usePhoneActions } = createNamespacedHelpers('phone');
const { useState: useStateApp } = createNamespacedHelpers('app');

export default {
  name: 'ClientInfo',
  components: {
    FontAwesomeIcon,
    EditClientInfo,
    PrimaryButton
  },

  setup(props, { root }) {
    const { getClientDetail } = useActions(['getClientDetail']);
    const { targetingClient: client } = useState(['targetingClient']);
    const { toggleEditClientModal: toggleEditClient } = useModalActions(['toggleEditClientModal']);
    const { startOutboundCall, setRef } = usePhoneActions(['startOutboundCall', 'setRef']);
    const { branches } = useStateApp(['branches']);

    const { id: clientId } = root.$route.params;

    const getBranchName = (branchId) => {
      const branch = branches.value.find((b) => b.id === branchId);
      return branch?.name || '';
    };

    const showEditClient = () => {
      toggleEditClient();
    };

    const startCall = (phoneNumber, id) => {
      setRef({ type: CALL_REFS.CLIENT, refId: id });
      startOutboundCall(phoneNumber);
    };

    onMounted(() => {
      getClientDetail(clientId);
    });

    return {
      faPhoneAlt,
      moment,
      DATE_FORMAT,
      DISPLAY_DATE_FORMAT,
      showEditClient,
      startCall,
      client,
      getBranchName
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#client-info {
  padding: 15px;
  background-color: $babyPowder;
  border: 1px solid $alto;
  border-radius: 4px;

  .client-id {
    .finflux-id {
      font-size: 34px;
      vertical-align: middle;
      color: $blueNcs;
    }
  }

  .info {
    .name {
      font-size: 18px;
      text-transform: uppercase;
      font-weight: bold;
    }

    table.details {
      margin-top: 20px;
      font-weight: normal;
      width: 100%;

      tr {
        td {
          padding: 5px;

          &:first-child {
            vertical-align: top;
          }

          &:last-child {
            text-align: right;
            padding-left: 10px;
          }

          a {
            text-decoration: none;
            font-weight: bold;
            color: $paradisePink;
          }
        }
      }
    }
  }

  .call {
    cursor: pointer;
    color: $blueNcs;
    margin-left: 10px;
  }

  .primary-button {
    margin-left: 10px;
    margin-bottom: 7px;
    border-radius: 4px;
    padding-left: 20px;
    padding-right: 20px;

    &.yellow {
      background-color: $yellowCrayola;
    }
  }

  @media (max-width: 767px) {
    margin-top: 15px;
  }
}
</style>
